import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import styles from './MicrositeIntegrations.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import { Check, AlertCircle, RefreshCw } from 'lucide-react'

import MicrositePageHeader from '../components/MicrositePageHeader/MicrositePageHeader'
import TextInput from '../components/TextInput/TextInput'
import SelectInput from '../components/SelectInput/SelectInput'
import MicrositeVersionDrawer from '../components/MicrositeVersionDrawer/MicrositeVersionDrawer'

const PAYMENT_GATEWAY_OPTIONS = [
    { value: '', label: 'None' },
    { value: 'razorpay', label: 'Razorpay' },
    { value: 'payu', label: 'PayU' },
    { value: 'cashfree', label: 'Cashfree' }
]

const EMAIL_PROVIDER_OPTIONS = [
    { value: '', label: 'None' },
    { value: 'ses', label: 'Amazon SES' },
    { value: 'sendgrid', label: 'SendGrid' },
    { value: 'mailgun', label: 'Mailgun' }
]

const SMS_PROVIDER_OPTIONS = [
    { value: '', label: 'None' },
    { value: 'msg91', label: 'MSG91' },
    { value: 'twilio', label: 'Twilio' },
    { value: 'textlocal', label: 'Textlocal' }
]

const EMPTY_FORM = {
    paymentGateway: '',
    emailProvider: '',
    smsProvider: '',
    storageProvider: '',
    cdnProvider: '',
    googleAnalyticsId: '',
    metaPixelId: '',
    customTrackingUrl: '',
    providerKeys: {}
}

const MicrositeIntegrations = () => {
    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [toast, setToast] = useState(null)
    const [historyOpen, setHistoryOpen] = useState(false)

    const [form, setForm] = useState(EMPTY_FORM)
    const [original, setOriginal] = useState(EMPTY_FORM)

    const isDirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(original), [form, original])

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3000)
    }

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: JSON.parse(authCtx?.loginData)?.partnersData?.vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, activeStore])

    const loadIntegrations = useCallback(async () => {
        setLoading(true)
        const res = await post('micrositeGetStoreConfig')
        if (res?.status === 'success') {
            const i = res.response?.integrations || {}
            const next = {
                paymentGateway: i.paymentGateway || '',
                emailProvider: i.emailProvider || '',
                smsProvider: i.smsProvider || '',
                storageProvider: i.storageProvider || '',
                cdnProvider: i.cdnProvider || '',
                googleAnalyticsId: i.googleAnalyticsId || '',
                metaPixelId: i.metaPixelId || '',
                customTrackingUrl: i.customTrackingUrl || '',
                providerKeys: i.providerKeys || {}
            }
            setForm(next)
            setOriginal(next)
        } else {
            showToast('Could not load integrations. Please refresh.', 'error')
        }
        setLoading(false)
    }, [post])

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadIntegrations()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier])

    const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }))

    const handleSave = async () => {
        setSaving(true)
        const res = await post('micrositeUpdateStoreIntegrations', { ...form })
        setSaving(false)

        if (res?.status === 'success') {
            setOriginal(form)
            showToast('Integrations saved successfully')
        } else {
            showToast('Could not save integrations. Please try again.', 'error')
        }
    }

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <RefreshCw size={18} className={styles.spinner} />
                <span>Loading integrations…</span>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            {toast && (
                <div className={`${styles.toast} ${styles[`toast_${toast.type}`]}`}>
                    {toast.type === 'success' ? <Check size={14} /> : <AlertCircle size={14} />}
                    {toast.message}
                </div>
            )}

            <MicrositePageHeader
                title="Integrations"
                subtitle="Connect payment, email, SMS and analytics providers for your microsite."
                isDirty={isDirty}
                saving={saving}
                onSave={handleSave}
                onHistory={() => setHistoryOpen(true)}
            />

            <section className={styles.fieldGroup}>
                <h2 className={styles.groupTitle}>Providers</h2>
                <p className={styles.groupDesc}>Choose which services power payments, email and SMS on your site.</p>

                <SelectInput
                    label="Payment gateway"
                    value={form.paymentGateway}
                    onChange={(v) => update('paymentGateway', v)}
                    options={PAYMENT_GATEWAY_OPTIONS}
                />
                <SelectInput
                    label="Email provider"
                    value={form.emailProvider}
                    onChange={(v) => update('emailProvider', v)}
                    options={EMAIL_PROVIDER_OPTIONS}
                />
                <SelectInput
                    label="SMS provider"
                    value={form.smsProvider}
                    onChange={(v) => update('smsProvider', v)}
                    options={SMS_PROVIDER_OPTIONS}
                />
            </section>

            <section className={styles.fieldGroup}>
                <h2 className={styles.groupTitle}>Analytics & tracking</h2>
                <p className={styles.groupDesc}>Connect Google Analytics, Meta Pixel, or a custom tracking endpoint.</p>

                <TextInput
                    label="Google Analytics ID"
                    value={form.googleAnalyticsId}
                    onChange={(v) => update('googleAnalyticsId', v)}
                    placeholder="G-XXXXXXXXXX"
                />
                <TextInput
                    label="Meta Pixel ID"
                    value={form.metaPixelId}
                    onChange={(v) => update('metaPixelId', v)}
                    placeholder="1234567890123456"
                />
                <TextInput
                    label="Custom tracking URL"
                    value={form.customTrackingUrl}
                    onChange={(v) => update('customTrackingUrl', v)}
                    placeholder="https://track.yourdomain.com/pixel.js"
                    helperText="Advanced — only set this if you have a custom analytics endpoint"
                />
            </section>

            <section className={styles.fieldGroup}>
                <h2 className={styles.groupTitle}>Storage & delivery</h2>
                <p className={styles.groupDesc}>Where your images and media are stored and served from.</p>

                <TextInput
                    label="Storage provider"
                    value={form.storageProvider}
                    onChange={(v) => update('storageProvider', v)}
                    placeholder="e.g. s3, cloudinary"
                />
                <TextInput
                    label="CDN provider"
                    value={form.cdnProvider}
                    onChange={(v) => update('cdnProvider', v)}
                    placeholder="e.g. cloudflare, bunnycdn"
                />
            </section>

            <MicrositeVersionDrawer
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                tabKey="integrations"
                storeIdentifier={activeStore?.storeIdentifier}
                vendorIdentifier={activeStore?.vendorIdentifier}
                onRolledBack={loadIntegrations}
            />
        </div>
    )
}

export default MicrositeIntegrations